import { debounce } from "lodash-es";
import { inventoryAutocompleteLotCodesUserPath } from "routes";
import { onViewLoad } from "~/jose_modern/on_view_load";
import { once } from "~/jose_modern/once";
import {
  AutocompleteState,
  createAutocompleteDropdown,
  renderAutocompleteSuggestions,
  updateAutocompleteSelection,
  showAutocompleteDropdown,
  hideAutocompleteDropdown,
  setupAutocompleteItemSelection,
  setupAutocompleteKeyboardNavigation,
  setupAutocompleteClickOutside,
  setupFloatingDropdown,
} from "~/jose_modern/inventory/shared/lot_code_typeahead";

const DEBOUNCE_DELAY = 250;
const MIN_QUERY_LENGTH = 1;

export const setupLotCodeAutocomplete = (selector, type, userId) => {
  const autocompleteLotCodeUrl = inventoryAutocompleteLotCodesUserPath(userId);

  // Track request order to prevent stale responses
  let lastRequestId = 0;
  let abortController = null;

  const setupAutocomplete = () => {
    const element = document.querySelector(selector);
    if (!element) return;

    once(element, "autocomplete", (_el) => {
      const state = new AutocompleteState();
      const suggestionsId = `${element.id}-suggestions`;
      const dropdown = createAutocompleteDropdown(element, suggestionsId);
      const cleanupFloating = setupFloatingDropdown(element, dropdown);

      // Cache server results for client-side filtering while new results load
      let allItems = [];

      const renderSuggestions = ({ showUpdating = false } = {}) => {
        renderAutocompleteSuggestions(dropdown, state.suggestions, {
          showUpdating,
        });
      };

      const showSuggestions = () => {
        showAutocompleteDropdown(element, dropdown);
      };

      const hideSuggestions = () => {
        hideAutocompleteDropdown(element, dropdown);
        state.reset();
      };

      const selectItem = (index) => {
        if (index >= 0 && index < state.suggestions.length) {
          element.value = state.suggestions[index];
          hideSuggestions();
        }
      };

      const submitSearchForm = () => {
        const form = element.closest("form");
        if (form) {
          form.requestSubmit();
        }
      };

      const showMessage = (message, cssClass = "autocomplete-status") => {
        const content = dropdown.querySelector(".dropdown-content");
        content.innerHTML = `<div class="dropdown-item ${cssClass}">${message}</div>`;
        showSuggestions();
      };

      const showSearching = () => {
        showMessage(
          '<span class="icon is-small"><i class="fas fa-spinner-scale fa-pulse"></i></span><span>Searching...</span>'
        );
      };

      const updateDisplay = (query) => {
        if (allItems.length === 0) return;

        // Client-side filter while waiting for server results
        state.suggestions = allItems.filter((item) =>
          item.toLowerCase().includes(query.toLowerCase())
        );

        state.selectedIndex = -1;

        if (state.suggestions.length > 0) {
          renderSuggestions({ showUpdating: true });
        } else {
          showSearching();
        }
      };

      const handleInput = debounce(function () {
        const q = this.value.trim();
        if (q.length >= MIN_QUERY_LENGTH) {
          if (abortController) {
            try {
              abortController.abort();
            } catch (e) {
              // Ignore abort errors besides debug log
              console.debug("Aborted previous request", e);
            }
          }

          abortController = new AbortController();
          const currentRequestId = ++lastRequestId;

          fetch(`${autocompleteLotCodeUrl}?q=${encodeURIComponent(q)}&type=${type}`, {
            signal: abortController.signal,
          })
            .then((response) => {
              if (abortController.signal.aborted) return;

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            })
            .then((items) => {
              // Ignore if aborted or stale response
              if (!items || abortController.signal.aborted || currentRequestId !== lastRequestId)
                return;

              if (items.length === 0) {
                state.reset();
                showMessage("No matches found");
                return;
              }

              allItems = items;
              state.suggestions = items;
              state.selectedIndex = -1;

              renderSuggestions({ showUpdating: false });
              showSuggestions();
            })
            .catch((e) => {
              // Silently ignore aborted requests
              if (e.name === "AbortError") return;

              // Only handle if this is the latest request
              if (currentRequestId === lastRequestId) {
                console.error(`Error loading suggestions: ${e.message}`);
                state.reset();
                showMessage("Error loading suggestions", "has-text-danger");
              }
            });
        } else {
          hideSuggestions();
        }
      }, DEBOUNCE_DELAY);

      element.addEventListener("input", function (e) {
        const q = e.target.value.trim();
        if (q.length >= MIN_QUERY_LENGTH) {
          if (state.suggestions.length > 0) {
            // Show filtered results immediately while waiting for server
            updateDisplay(q);
          } else if (dropdown.classList.contains("is-hidden")) {
            showSearching();
          }
        }
        handleInput.call(this);
      });

      element.addEventListener("focus", function (e) {
        const q = e.target.value.trim();
        if (q.length >= MIN_QUERY_LENGTH && dropdown.classList.contains("is-hidden")) {
          // Trigger autocomplete on focus if there's existing text
          showSearching();
          handleInput.call(this);
        }
      });

      setupAutocompleteItemSelection(dropdown, {
        onSelect: selectItem,
        onHover: (index) => {
          state.selectedIndex = index;
          updateAutocompleteSelection(dropdown, state.selectedIndex, element);
        },
      });

      setupAutocompleteKeyboardNavigation(element, dropdown, {
        state,
        onSelect: selectItem,
        onCancel: hideSuggestions,
        onEnterWithNoSelection: () => {
          hideSuggestions();
          submitSearchForm();
        },
      });

      const cleanupClickOutside = setupAutocompleteClickOutside(element, dropdown, hideSuggestions);

      const form = element.closest("form");
      let cleanupFormSubmit = null;

      if (form) {
        const submitHandler = (_e) => {
          element.value = element.value.trim();
        };

        form.addEventListener("submit", submitHandler);
        cleanupFormSubmit = () => form.removeEventListener("submit", submitHandler);
      }

      element._cleanup = () => {
        cleanupClickOutside();
        if (abortController) {
          abortController.abort();
          abortController = null;
        }
        handleInput.cancel(); // Cancel any pending debounced calls
        cleanupFloating();
        if (dropdown.parentNode) dropdown.parentNode.removeChild(dropdown);
        if (cleanupFormSubmit) cleanupFormSubmit();
      };
    });
  };

  setupAutocomplete();
};

const getUserId = () => window.location.pathname.split("/")[2];

onViewLoad(["users#inventory", "users#lot_code_search"], () => {
  const userId = getUserId();

  setupLotCodeAutocomplete("#ingredient_lot_code_search", "ingredient", userId);
  setupLotCodeAutocomplete("#finished_good_lot_code_search", "finished_good", userId);
});
